import { ownedActionConfig } from '../lib/accountBoundary';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  addScheduleOccurrenceRosterAthlete,
  applyScheduleOccurrenceSlotFiller,
  archiveScheduleProgram,
  cancelScheduleOccurrenceRosterBookingAndSuggest,
  checkoutScheduleOccurrenceAttendance,
  clearScheduleOccurrenceAttendance,
  createScheduleAthleteAvailability,
  createScheduleCoachAvailability,
  createScheduleProgram,
  deleteScheduleAthleteAvailability,
  deleteScheduleCoachAvailability,
  generateSmartScheduleSuggestions,
  finishScheduleOccurrenceAttendanceRecap,
  getScheduleOccurrenceAttendanceRecap,
  getScheduleAthleteAvailability,
  getScheduleDashboard,
  getScheduleCoachAvailability,
  getScheduleOccurrenceDetail,
  getScheduleOccurrenceSlotFillers,
  getScheduleProgramRegistrations,
  getSchedulePrograms,
  markScheduleOccurrenceBookedRosterPresent,
  restoreScheduleProgram,
  saveScheduleOccurrenceAttendanceRecap,
  updateScheduleAthleteAvailability,
  updateScheduleCoachAvailability,
  updateScheduleOccurrenceAttendance,
  updateScheduleOccurrenceMakeupCredit,
  updateScheduleOccurrenceProgressNote,
  updateScheduleOccurrenceRosterBooking,
  updateScheduleProgramRegistration,
  updateScheduleProgram,
  getScheduleBookableOfferings,
  createScheduleBookableOffering,
  updateScheduleBookableOffering,
} from '../lib/schedule';
import type {
  ScheduleDashboardResponse,
  TenantAthleteAvailabilityListResponse,
  TenantAthleteAvailabilityPayload,
  TenantCoachAvailability,
  TenantCoachAvailabilityPayload,
  TenantMakeupCreditUpdatePayload,
  TenantProgram,
  TenantProgramRegistration,
  TenantProgramRegistrationUpdatePayload,
  TenantProgramUpsertPayload,
  TenantRosterCancellationPayload,
  TenantSessionFollowUpResponse,
  TenantSessionDetailResponse,
  TenantSessionAttendanceUpdatePayload,
  TenantSessionProgressNoteUpdatePayload,
  TenantSessionRecapPayload,
  TenantSessionRecapResponse,
  TenantSessionRosterAddPayload,
  TenantSessionRosterUpdatePayload,
  TenantSlotFillerApplyPayload,
  TenantSlotFillerSuggestionResponse,
  TenantSmartSchedulePayload,
  TenantSmartScheduleSuggestionResponse,
  TenantBookableOffering,
  TenantBookableOfferingPayload,
} from '../types';

export const scheduleKeys = {
  all: ['schedule'] as const,
  dashboard: () => [...scheduleKeys.all, 'dashboard'] as const,
  availability: () => [...scheduleKeys.all, 'availability'] as const,
  athleteAvailability: () => [...scheduleKeys.all, 'athlete-availability'] as const,
  programs: () => [...scheduleKeys.all, 'programs'] as const,
  programRegistrations: () => [...scheduleKeys.all, 'program-registrations'] as const,
  occurrenceDetail: (occurrenceId?: number | null) => [...scheduleKeys.all, 'occurrence-detail', occurrenceId] as const,
  attendanceRecap: (occurrenceId?: number | null, bookingId?: number | null) =>
    [...scheduleKeys.all, 'attendance-recap', occurrenceId, bookingId] as const,
  slotFillers: (occurrenceId?: number | null) => [...scheduleKeys.all, 'slot-fillers', occurrenceId] as const,
  bookableOfferings: () => [...scheduleKeys.all, 'bookable-offerings'] as const,
};

export function useScheduleBookableOfferings(enabled: boolean = true) {
  return useQuery<TenantBookableOffering[]>({
    queryKey: scheduleKeys.bookableOfferings(),
    queryFn: getScheduleBookableOfferings,
    staleTime: 30 * 1000,
    retry: false,
    enabled,
  });
}

export function useSaveScheduleBookableOffering() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ offeringId, payload }: { offeringId?: number; payload: TenantBookableOfferingPayload }) =>
      offeringId
        ? updateScheduleBookableOffering(offeringId, payload)
        : createScheduleBookableOffering(payload),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: scheduleKeys.bookableOfferings() });
    },
  });
}

export function useScheduleDashboard(enabled: boolean = true) {
  return useQuery<ScheduleDashboardResponse>({
    queryKey: scheduleKeys.dashboard(),
    queryFn: getScheduleDashboard,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useSchedulePrograms(enabled: boolean = true) {
  return useQuery<TenantProgram[]>({
    queryKey: scheduleKeys.programs(),
    queryFn: getSchedulePrograms,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useScheduleProgramRegistrations(enabled: boolean = true) {
  return useQuery<TenantProgramRegistration[]>({
    queryKey: scheduleKeys.programRegistrations(),
    queryFn: getScheduleProgramRegistrations,
    staleTime: 30 * 1000,
    retry: false,
    enabled,
  });
}

export function useScheduleCoachAvailability(enabled: boolean = true) {
  return useQuery<TenantCoachAvailability[]>({
    queryKey: scheduleKeys.availability(),
    queryFn: getScheduleCoachAvailability,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useCreateScheduleCoachAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantCoachAvailabilityPayload) => createScheduleCoachAvailability(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateScheduleCoachAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      availabilityId,
      payload,
    }: {
      availabilityId: number;
      payload: TenantCoachAvailabilityPayload;
    }) => updateScheduleCoachAvailability(availabilityId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useDeleteScheduleCoachAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (availabilityId: number) => deleteScheduleCoachAvailability(availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useScheduleAthleteAvailability(enabled: boolean = true) {
  return useQuery<TenantAthleteAvailabilityListResponse>({
    queryKey: scheduleKeys.athleteAvailability(),
    queryFn: getScheduleAthleteAvailability,
    staleTime: 60 * 1000,
    retry: false,
    enabled,
  });
}

export function useCreateScheduleAthleteAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantAthleteAvailabilityPayload) => createScheduleAthleteAvailability(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateScheduleAthleteAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      availabilityId,
      payload,
    }: {
      availabilityId: number;
      payload: TenantAthleteAvailabilityPayload;
    }) => updateScheduleAthleteAvailability(availabilityId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useDeleteScheduleAthleteAvailability() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (availabilityId: number) => deleteScheduleAthleteAvailability(availabilityId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useGenerateSmartScheduleSuggestions() {
  return useMutation<TenantSmartScheduleSuggestionResponse, Error, TenantSmartSchedulePayload>({
    mutationFn: (payload: TenantSmartSchedulePayload) => generateSmartScheduleSuggestions(payload),
  });
}

export function useScheduleOccurrenceDetail(occurrenceId: number | null, enabled: boolean = true) {
  return useQuery<TenantSessionDetailResponse>({
    queryKey: scheduleKeys.occurrenceDetail(occurrenceId),
    queryFn: () => getScheduleOccurrenceDetail(occurrenceId as number),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && occurrenceId != null,
  });
}

export function useScheduleAttendanceRecap(
  occurrenceId: number | null,
  bookingId: number | null,
  enabled: boolean = true
) {
  return useQuery<TenantSessionRecapResponse>({
    queryKey: scheduleKeys.attendanceRecap(occurrenceId, bookingId),
    queryFn: () => getScheduleOccurrenceAttendanceRecap(occurrenceId as number, bookingId as number),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && occurrenceId != null && bookingId != null,
  });
}

export function useScheduleSlotFillers(occurrenceId: number | null, enabled: boolean = true) {
  return useQuery<TenantSlotFillerSuggestionResponse>({
    queryKey: scheduleKeys.slotFillers(occurrenceId),
    queryFn: () => getScheduleOccurrenceSlotFillers(occurrenceId as number),
    staleTime: 30 * 1000,
    retry: false,
    enabled: enabled && occurrenceId != null,
  });
}

export function useCreateScheduleProgram() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: TenantProgramUpsertPayload) => createScheduleProgram(payload, owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateScheduleProgram() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ programId, payload }: { programId: number; payload: TenantProgramUpsertPayload }) =>
      updateScheduleProgram(programId, payload, owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useArchiveScheduleProgram() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (programId: number) => archiveScheduleProgram(programId, owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useRestoreScheduleProgram() {
  const owner = ownedActionConfig();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (programId: number) => restoreScheduleProgram(programId, owner),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    },
  });
}

export function useUpdateScheduleProgramRegistration() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      registrationId,
      payload,
    }: {
      registrationId: number;
      payload: TenantProgramRegistrationUpdatePayload;
    }) => updateScheduleProgramRegistration(registrationId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.programRegistrations() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.programs() });
    },
  });
}

export function useAddScheduleRosterAthlete() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ occurrenceId, payload }: { occurrenceId: number; payload: TenantSessionRosterAddPayload }) =>
      addScheduleOccurrenceRosterAthlete(occurrenceId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.slotFillers(variables.occurrenceId) });
    },
  });
}

export function useUpdateScheduleRosterBooking() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantSessionRosterUpdatePayload;
    }) => updateScheduleOccurrenceRosterBooking(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.slotFillers(variables.occurrenceId) });
    },
  });
}

export function useCancelScheduleRosterBookingAndSuggest() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantSessionFollowUpResponse,
    Error,
    { occurrenceId: number; bookingId: number; payload?: TenantRosterCancellationPayload }
  >({
    mutationFn: ({ occurrenceId, bookingId, payload }) =>
      cancelScheduleOccurrenceRosterBookingAndSuggest(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.slotFillers(variables.occurrenceId) });
    },
  });
}

export function useApplyScheduleSlotFiller() {
  const queryClient = useQueryClient();
  return useMutation<
    TenantSessionFollowUpResponse,
    Error,
    { occurrenceId: number; payload: TenantSlotFillerApplyPayload }
  >({
    mutationFn: ({ occurrenceId, payload }) => applyScheduleOccurrenceSlotFiller(occurrenceId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.slotFillers(variables.occurrenceId) });
    },
  });
}

export function useUpdateScheduleAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantSessionAttendanceUpdatePayload;
    }) => updateScheduleOccurrenceAttendance(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.slotFillers(variables.occurrenceId) });
    },
  });
}

export function useClearScheduleAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ occurrenceId, bookingId }: { occurrenceId: number; bookingId: number }) =>
      clearScheduleOccurrenceAttendance(occurrenceId, bookingId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}

export function useCheckoutScheduleAttendance() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ occurrenceId, bookingId }: { occurrenceId: number; bookingId: number }) =>
      checkoutScheduleOccurrenceAttendance(occurrenceId, bookingId),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}

export function useSaveScheduleAttendanceRecap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantSessionRecapPayload;
    }) => saveScheduleOccurrenceAttendanceRecap(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.attendanceRecap(variables.occurrenceId, variables.bookingId),
      });
    },
  });
}

export function useFinishScheduleAttendanceRecap() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantSessionRecapPayload;
    }) => finishScheduleOccurrenceAttendanceRecap(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
      queryClient.invalidateQueries({
        queryKey: scheduleKeys.attendanceRecap(variables.occurrenceId, variables.bookingId),
      });
    },
  });
}

export function useMarkScheduleBookedRosterPresent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (occurrenceId: number) => markScheduleOccurrenceBookedRosterPresent(occurrenceId),
    onSuccess: (_data, occurrenceId) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(occurrenceId) });
    },
  });
}

export function useUpdateScheduleProgressNote() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantSessionProgressNoteUpdatePayload;
    }) => updateScheduleOccurrenceProgressNote(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}

export function useUpdateScheduleMakeupCredit() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      occurrenceId,
      bookingId,
      payload,
    }: {
      occurrenceId: number;
      bookingId: number;
      payload: TenantMakeupCreditUpdatePayload;
    }) => updateScheduleOccurrenceMakeupCredit(occurrenceId, bookingId, payload),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: scheduleKeys.dashboard() });
      queryClient.invalidateQueries({ queryKey: scheduleKeys.occurrenceDetail(variables.occurrenceId) });
    },
  });
}
